import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Inbox } from 'lucide-react';
import { getAuthToken } from '../../auth/context/AuthContext';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/platform`;

const TABS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_BADGE = {
  new: 'badge-blue',
  contacted: 'badge-yellow',
  closed: 'badge-gray',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

// Show a typed website as a link even when the scheme was left off.
const siteHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/** "Request a demo" enquiries sent from the marketing landing page. */
export default function DemoRequests() {
  const [data, setData] = useState({ requests: [], counts: {} });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API}/demo-requests${status ? `?status=${status}` : ''}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Failed to load demo requests (${res.status})`);
      setData(body);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, next) => {
    setSaving(id);
    setErr('');
    try {
      const res = await fetch(`${API}/demo-requests/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'Could not update the status');
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(null);
    }
  };

  const counts = data.counts || {};

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Demo Requests</div>
          <div className="page-subtitle">Enquiries from the “Request a demo” form on the landing page</div>
        </div>
      </div>

      <div role="tablist" aria-label="Filter by status" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {TABS.map(t => {
          const active = status === t.value;
          const n = t.value ? counts[t.value] : counts.all;
          return (
            <button
              key={t.value || 'all'}
              type="button"
              role="tab"
              aria-selected={active}
              className={`btn btn-sm ${active ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setStatus(t.value)}
            >
              {t.label}{n != null ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertTriangle size={14} strokeWidth={2} /> {err}
        </div>
      )}

      {loading ? (
        <div className="empty-state"><div className="empty-state-text">Loading…</div></div>
      ) : data.requests.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon"><Inbox size={40} strokeWidth={1.5} /></div>
            <div className="empty-state-title">No demo requests{status ? ` marked ${status}` : ' yet'}</div>
            <div className="empty-state-text">Requests sent from the landing page appear here.</div>
          </div>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Received</th><th>Name</th><th>Company</th><th>Contact</th><th>Hiring for</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.requests.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</td>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td>
                    {r.company_name || '—'}
                    {r.website && (
                      <div style={{ fontSize: 12 }}>
                        <a href={siteHref(r.website)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{r.website}</a>
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    <a href={`mailto:${r.email}`} style={{ color: 'var(--accent)' }}>{r.email}</a>
                    {r.phone && <div style={{ color: 'var(--text-muted)' }}>{r.phone}</div>}
                  </td>
                  <td style={{ fontSize: 13, maxWidth: 280, whiteSpace: 'pre-wrap' }}>{r.hiring_for || '—'}</td>
                  <td>
                    <select
                      className={`badge ${STATUS_BADGE[r.status] || 'badge-gray'}`}
                      value={r.status}
                      disabled={saving === r.id}
                      onChange={e => changeStatus(r.id, e.target.value)}
                      aria-label={`Status for ${r.name}`}
                      style={{ border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 600 }}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
